import { ChainId, chainSettings, ChainType } from "@/constants/chain-specific"
import { MASTERCHEFS_CONFIG } from "./masterchef"
import type { DexConfig } from "./types"
import { tokenAbi } from "@/constants/abi"
import { chewyRouterAbi } from "@/constants/abi/chewyRouterAbi"
import { chewyFactoryAbi } from "@/constants/abi/chewyFactoryAbi"
import { RequiredAggregatorChainType } from "../project"
import v4UniversalRouterAbi from "@/constants/abi/v4UniversalRouterAbi"
import v4PoolManagerAbi from "@/constants/abi/v4PoolManagerAbi"
import v4PositionManagerAbi from "@/constants/abi/v4PositionManagerAbi"
import v4StateViewAbi from "@/constants/abi/v4StateViewAbi"
import v4PositionDescriptorAbi from "@/constants/abi/v4PositionDescriptor"
import v4QuoterAbi from "@/constants/abi/v4QuoterAbi"
import { velodromeFactoryAbi } from "@/constants/abi/velodromeFactoryAbi"
import { velodromeRouterAbi } from "@/constants/abi/velodromeRouterAbi"
import { fraxRouterAbi } from "@/constants/abi/fraxRouterAbi"
import { fraxFactoryAbi } from "@/constants/abi/fraxFactoryAbi"
import { SHIB_TOKENS } from "../tokens";

type DexConfigListType = Record<string, DexConfig>

export const DEX_LIST: DexConfigListType = {
    chewyswap: {
        name: "ChewySwap",
        slug: "chewyswap",
        url: "https://chewyswap.dog",
        router: {
            address: "0x2875F2D86d83635A859029872e745581530cEec7",
            abi: chewyRouterAbi
        },
        factory: {
            address: "0xEDedDbde5ffA62545eDF97054edC11013ED72125",
            abi: chewyFactoryAbi,
            codeHash: "0xa2ef75f706f4aa0c026dd9371dab1bac975a91bc8f0852a86e6f330eedd30848"
        },
        lpName: "Chewy LPs",
        lpSymbol: "ChewyLP",
        type: "v2",
        logo: "https://chewyswap.dog/logo.png",
        addLpUrl: "https://chewyswap.dog/add/",
        swapUrl: "https://chewyswap.dog/swap/",
        weth: chainSettings[ChainId.SHIBARIUM].wNative,
        status: "primary"
    },
    chewyswapv4: {
        name: "ChewySwap",
        slug: "chewyswap",
        url: "https://chewyswap.dog",
        router: {
            address: "0x8faFF0d9989Ac0BBfDFC8BD66A50f821e4E0ef58",
            abi: v4UniversalRouterAbi
        },
        factory: {
            address: "0xEDedDbde5ffA62545eDF97054edC11013ED72125",
            abi: chewyFactoryAbi
        },
        lpName: "Chewy LPs",
        lpSymbol: "ChewyLP",
        type: "v2",
        logo: "https://chewyswap.dog/logo.png",
        addLpUrl: "https://chewyswap.dog/add/",
        swapUrl: "https://chewyswap.dog/swap/",
        weth: chainSettings[ChainId.SHIBARIUM].wNative
    },
    shibaswapv1: {
        name: "ShibaSwap",
        slug: "shibaswapv1",
        url: "https://shibaswap.com",
        router: {
            address: "0xEF83bbB63E8A7442E3a4a5d28d9bBf32D7c813c8",
            abi: chewyRouterAbi
        },
        factory: {
            address: "0xc2b4218F137e3A5A9B98ab3AE804108F0D312CBC",
            abi: chewyFactoryAbi,
            codeHash: "0x65d1a3b1e46c6e4f1be1ad5f99ef14dc488ae0549dc97db9b30afe2241ce1c7a"
        },
        lpName: "SLP",
        lpSymbol: "SLP",
        type: "v2",
        logo: "https://dd.dexscreener.com/ds-data/dexes/shibaswap.png",
        addLpUrl: "https://chewyswap.dog/add/",
        swapUrl: "https://chewyswap.dog/swap/",
    },
    shibaswapv2: {
        name: "ShibaSwap V2",
        slug: "shibaswapv2",
        url: "https://shibaswap.com",
        router: {
            address: "0x59D4dDaB6a1d9A0A84e3f8C06a720dE2404AA58a",
            abi: chewyRouterAbi
        },
        factory: {
            address: "0x2996B636663ddeBaE28742368ed47b57539C9600",
            abi: chewyFactoryAbi
        },
        lpName: "SLP",
        lpSymbol: "SLP",
        type: "v3",
        quoter: {
            address: "0x3fA2664876ecE312cf29faa36fd42d76866E580F",
            abi: chewyRouterAbi // not set
        },
        logo: "https://dd.dexscreener.com/ds-data/dexes/shibaswap.png",
        addLpUrl: "https://chewyswap.dog/add/",
        swapUrl: "https://chewyswap.dog/swap/",
    },
    woofswap: {
        name: "WoofSwap",
        slug: "woofswap",
        url: "https://www.woofswap.finance/",
        router: {
            address: "0x96b16aBD53Bfd765F4CD118590C1d0be8B57DE24",
            abi: velodromeRouterAbi
        },
        factory: {
            address: "0xB9fbdFA27B7ba8BB2d4bB4aB399e4c55F0F7F83a",
            abi: velodromeFactoryAbi,
            codeHash: "0x6ee2d23055d4b72d42ed84dab4c4522e3190dc0864eb916c082c507fc686b8d2"
        },
        weth: SHIB_TOKENS.woofwbone, // Wrapped Bone WOOFSWAP
        lpName: "VolatileV1 AMM",
        lpSymbol: "vAMM",
        type: "velodrome",
        logo: "https://dd.dexscreener.com/ds-data/dexes/woofswap.png",
        addLpUrl: "https://www.woofswap.finance/liquidity/manage/",
        swapUrl: "https://www.woofswap.finance/swap/",
    },
    woofswappro: {
        name: "WoofSwap Pro",
        slug: "woofswappro",
        url: "https://app.woofswap.finance/",
        router: {
            address: "0xEF83bbB63E8A7442E3a4a5d28d9bBf32D7c813c8",
            abi: chewyRouterAbi
        },
        factory: {
            address: "0x5c6C40CAe6f57b782D8Ff445258989aaC73D5074",
            abi: chewyFactoryAbi,
            codeHash: "0x17c5b992b24fc7e8a68e8a82964ddc4ecba9aaed1c088e5b35eea7a3b349b670"
        },
        lpName: "any",
        lpSymbol: "any",
        type: "v2",
        logo: "https://dd.dexscreener.com/ds-data/dexes/woofswap.png",
        addLpUrl: "https://app.woofswap.finance/liquidity/manage",
        swapUrl: "https://app.woofswap.finance/swap/",
    },
    marswap: {
        name: "Marswap",
        slug: "marswap",
        url: "https://shibaswap.com",
        router: {
            address: "0x59D4dDaB6a1d9A0A84e3f8C06a720dE2404AA58a",
            abi: chewyRouterAbi
        },
        factory: {
            address: "0xBe0223f65813C7c82E195B48F8AAaAcb304FbAe7",
            abi: chewyFactoryAbi,
            codeHash: "0xda4cfadda9109690cd041f79bfa61400869a7db219562aab3955b452f5ecf8b8"
        },
        lpName: "SLP",
        lpSymbol: "SLP",
        logo: "https://dd.dexscreener.com/ds-data/dexes/marswap.png",
        addLpUrl: "https://chewyswap.dog/add/",
        swapUrl: "https://chewyswap.dog/swap/",
        wethUsdPair: "0xB69A4132af7495aE809Ce0155b7be1889ef72918",
        usdToken: {
            address: "0xaB082b8ad96c7f47ED70ED971Ce2116469954cFB",
            decimals: 6,
            symbol: "USDT",
            name: "Tether USD",
        },
        status: "rugged"
    },
    punkswap: {
        name: "PunkSwap",
        slug: "punkswap",
        url: "https://shibaswap.com",
        router: {
            address: "0x561Ef19238646Bbeb5f374E402892BfE5Ead1567",
            abi: chewyRouterAbi
        },
        factory: {
            address: "0x5640113EA7F369E6DAFbe54cBb1406E5BF153E90",
            abi: chewyFactoryAbi,
            codeHash: "0xde0be195ffcf9d98e98ba1aa415e4e6746b6cc3a1f593373eb5d7f1b133573bd"
        },
        lpName: "SLP",
        lpSymbol: "SLP",
        logo: "https://dd.dexscreener.com/ds-data/dexes/punkswap.png",
        addLpUrl: "https://chewyswap.dog/add/",
        swapUrl: "https://chewyswap.dog/swap/",
        status: "rugged"
    },

    // DOGECHAIN:
    chewyswapdc: {
        name: "ChewySwap DC",
        slug: "chewyswapdc",
        url: "https://chewyswap.dog",
        router: {
            address: "0x45AFCf57F7e3F3B9cA70335E5E85e4F77DcC5087",
            abi: chewyRouterAbi
        },
        factory: {
            address: "0x7C10a3b7EcD42dd7D79C0b9d58dDB812f92B574A",
            abi: chewyFactoryAbi
        },
        lpName: "DogeShrek Liquidity Token",
        lpSymbol: "dogeshrek-lp",
        logo: "https://chewyswap.dog/logo.png",
        addLpUrl: "https://chewyswap.dog/add/",
        swapUrl: "https://chewyswap.dog/swap/",
        type: "v2",
        status: "primary"
    },
    quickswapv2: {
        name: "QuickSwap V2",
        slug: "quickswap",
        url: "https://quickswap.exchange",
        router: {
            address: "0x2875F2D86d83635A859029872e745581530cEec7",
            abi: chewyRouterAbi
        },
        factory: {
            address: "0xEDedDbde5ffA62545eDF97054edC11013ED72125",
            abi: chewyFactoryAbi
        },
        lpName: "QLP",
        lpSymbol: "QLP",
        logo: "/images/dex/quickswap.svg",
        addLpUrl: "https://chewyswap.dog/add/",
        swapUrl: "https://chewyswap.dog/swap/",
        wethUsdPair: "0xFE550f3c3787aB3dffea9c315EA5a7b05960D256",
        usdToken: {
            address: "0xaB082b8ad96c7f47ED70ED971Ce2116469954cFB",
            decimals: 6,
            symbol: "USDT",
            name: "Tether USD",
        }
    },
    quickswapv3: {
        name: "QuickSwap V3",
        slug: "quickswap",
        url: "https://quickswap.exchange",
        router: {
            address: "0x2875F2D86d83635A859029872e745581530cEec7",
            abi: chewyRouterAbi
        },
        factory: {
            address: "0xEDedDbde5ffA62545eDF97054edC11013ED72125",
            abi: chewyFactoryAbi
        },
        lpName: "QLP",
        lpSymbol: "QLP",
        logo: "/images/dex/quickswap.webp",
        addLpUrl: "https://chewyswap.dog/add/",
        swapUrl: "https://chewyswap.dog/swap/",
        wethUsdPair: "0xFE550f3c3787aB3dffea9c315EA5a7b05960D256",
        usdToken: {
            address: "0xaB082b8ad96c7f47ED70ED971Ce2116469954cFB",
            decimals: 6,
            symbol: "USDT",
            name: "Tether USD",
        }
    },
    dogeswap: {
        name: "DogeSwap",
        slug: "dogeswap",
        url: "https://dogeswap.com",
        router: {
            address: "0x57315B8a5d628A8549bd56350CEFF24DEB4B1575",
            abi: chewyRouterAbi
        },
        factory: {
            address: "0xD27D9d61590874Bf9ee2a19b27E265399929C9C3",
            abi: chewyFactoryAbi,
            codeHash: "0xbfc2d4e06b5c176871b6144c7de089666b9ec72c1d8ba86002bfc8ea119b2419"
        },
        lpName: "DLP",
        lpSymbol: "DLP",
        logo: "https://dd.dexscreener.com/ds-data/dexes/dogeswap.png",
        addLpUrl: "https://chewyswap.dog/add/",
        swapUrl: "https://chewyswap.dog/swap/",
        wethUsdPair: "0xFE550f3c3787aB3dffea9c315EA5a7b05960D256",
        usdToken: {
            address: "0xaB082b8ad96c7f47ED70ED971Ce2116469954cFB",
            decimals: 6,
            symbol: "USDT",
            name: "Tether USD",
        }
    },
    fraxswap: {
        name: "FraxSwap",
        slug: "fraxswap",
        url: "https://fraxswap.com",
        router: {
            address: "0x0f6A5c5F341791e897eB1FB8fE8B4e30EC4F9bDf",
            abi: fraxRouterAbi
        },
        factory: {
            address: "0x67b7DA7c0564c6aC080f0A6D9fB4675e52E6bF1d",
            abi: fraxFactoryAbi,
            codeHash: "0xf7089f17c5efaa8f1bcf146d35a03dd5c3d2c72db1da99a1bf764ce31a866213"
        },
        lpName: "Fraxswap V1",
        lpSymbol: "FS-V1",
        logo: "https://dd.dexscreener.com/ds-data/dexes/fraxswap.png",
        addLpUrl: "https://chewyswap.dog/add/",
        swapUrl: "https://chewyswap.dog/swap/",
        wethUsdPair: "0xFE550f3c3787aB3dffea9c315EA5a7b05960D256",
        usdToken: {
            address: "0xB69A4132af7495aE809Ce0155b7be1889ef72918",
            decimals: 6,
            symbol: "USDT",
            name: "Tether USD",
        }
    },
    yodeswap: {
        name: "YodeSwap",
        slug: "yodeswap",
        url: "https://yodeswap.com",
        router: {
            address: "0x72d85Ab47fBfc5E7E04a8bcfCa1601D8f8cE1a50",
            abi: chewyRouterAbi
        },
        factory: {
            address: "0xAaA04462e35f3e40D798331657cA015169e005d7",
            abi: chewyFactoryAbi,
            codeHash: "0x746cdd6c24c5f1496602686ccbb848d5d9c8e5ccc408250c4cc8be8331de6857"
        },
        lpName: "YLP",
        lpSymbol: "YLP",
        logo: "https://dd.dexscreener.com/ds-data/dexes/yodeswap.png",
        addLpUrl: "https://chewyswap.dog/add/",
        swapUrl: "https://chewyswap.dog/swap/",
        wethUsdPair: "0xFE550f3c3787aB3dffea9c315EA5a7b05960D256",
        usdToken: {
            address: "0xB69A4132af7495aE809Ce0155b7be1889ef72918",
            decimals: 6,
            symbol: "USDT",
            name: "Tether USD",
        }
    },
    kibbleswap: {
        name: "KibbleSwap",
        slug: "kibbleswap",
        url: "https://kibbleswap.com",
        router: {
            address: "0x6258c967337D3faF0C2ba3ADAe5656bA95419d5f",
            abi: chewyRouterAbi
        },
        factory: {
            address: "0xF4bc79D32A7dEfd87c8A9C100FD83206bbF19Af5",
            abi: chewyFactoryAbi,
            codeHash: "0xcac27a3deb12764156265fb78d54dcb0d65ecc938272597dd22a45ed05e02e7a"
        },
        lpName: "KLP",
        lpSymbol: "KLP",
        logo: "https://dd.dexscreener.com/ds-data/dexes/kibbleswap.png",
        addLpUrl: "https://chewyswap.dog/add/",
        swapUrl: "https://chewyswap.dog/swap/",
        wethUsdPair: "0xFE550f3c3787aB3dffea9c315EA5a7b05960D256",
        usdToken: {
            address: "0xB69A4132af7495aE809Ce0155b7be1889ef72918",
            decimals: 6,
            symbol: "USDT",
            name: "Tether USD",
        }
    },
    wojak: {
        name: "Wojak",
        slug: "wojak",
        url: "https://wojak.fi",
        router: {
            address: "0x9695906B4502D5397E6D21ff222e2C1a9e5654a9",
            abi: chewyRouterAbi
        },
        factory: {
            address: "0xc7c86B4f940Ff1C13c736b697e3FbA5a6Bc979F9",
            abi: chewyFactoryAbi,
            codeHash: "0x7856aa433cee3d1a7f4b34cd0f0cf0967c2345e951944c19991f4a73d0f5eee6"
        },
        lpName: "Wojak LPs",
        lpSymbol: "Wojk-LP",
        type: "v2",
        logo: "/images/dex/wojak.webp",
        addLpUrl: "https://wojak.fi/add/",
        swapUrl: "https://wojak.fi/swap?chain=dogechain&outputCurrency=",
        wethUsdPair: "0xFE550f3c3787aB3dffea9c315EA5a7b05960D256",
        usdToken: {
            address: "0xB69A4132af7495aE809Ce0155b7be1889ef72918",
            decimals: 6,
            symbol: "USDT",
            name: "Tether USD",
        }
    },
    dogmoney: {
        name: "DogMoney",
        slug: "dogmoney",
        url: "https://dogmoney.io",
        logo: "https://icons.llamao.fi/icons/protocols/dogmoney?w=48&h=48",
        router: {
            address: "0x9BBF70e64fbe8Fc7afE8a5Ae90F2DB1165013F93",
            abi: chewyRouterAbi
        },
        factory: {
            address: "0xaF85e6eD0Da6f7F5F86F2f5A7d595B1b0F35706C",
            abi: chewyFactoryAbi,
            codeHash: "0x022c5c03794888783ccaeb987cd83b57b6e707f13c06fe761befb20653553b92"
        },
        lpName: "DOGMONEY LP Token",
        lpSymbol: "DLP",
        type: "v2",
        wethUsdPair: "0xFE550f3c3787aB3dffea9c315EA5a7b05960D256",
        usdToken: {
            address: "0xB69A4132af7495aE809Ce0155b7be1889ef72918",
            decimals: 6,
            symbol: "USDT",
            name: "Tether USD",
        }   
    }
}

export const DEX_LIST_BY_CHAIN: Record<RequiredAggregatorChainType, DexConfig[]> = {
    [109]: [DEX_LIST.chewyswap, DEX_LIST.woofswap, DEX_LIST.shibaswapv1, DEX_LIST.punkswap, DEX_LIST.woofswappro, DEX_LIST.marswap],
    [2000]: [DEX_LIST.kibbleswap, DEX_LIST.chewyswapdc, DEX_LIST.quickswapv2, DEX_LIST.dogeswap, DEX_LIST.yodeswap, DEX_LIST.wojak, DEX_LIST.dogmoney],

}